import {BigNumber, ContractReceipt, ContractTransaction} from 'ethers';
import {ReactNode, useCallback, useState} from 'react';
import {useTransactionStatus} from 'src/hooks';
import {ConditionalWrapper, ethStr} from 'src/utils';
import BusyButton, {BusyButtonProps} from './BusyButton';
import EthIcon from './EthIcon';
import Sparkles from './Sparkles';
import WaitButton, {WaitButtonProps} from './WaitButton';

type Props = BusyButtonProps &
  WaitButtonProps & {
    ButtonComponent?: typeof BusyButton | typeof WaitButton;
    eth?: BigNumber;
    sparkling?: boolean;
    onRunTransaction: (eth?: BigNumber) => Promise<ContractTransaction>;
    onReceipt?: (receipt: ContractReceipt) => void;
    onFail?: (err: any, message: string) => void;
    children: ReactNode | ((ready: boolean, timeLeft: string) => ReactNode);
  };
export default function TransactionButton(props: Props) {
  const [transactionHash, setTransactionHash] = useState<string | null>(null);
  useTransactionStatus(transactionHash);

  const {
    ButtonComponent = BusyButton,
    eth,
    sparkling = false,
    onClick,
    children,
    onRunTransaction,
    onReceipt,
    onFail,
    ...rest
  } = props;

  const handleClick = useCallback(
    async (e) => {
      try {
        onClick && onClick(e);
        const result = await onRunTransaction(eth);
        setTransactionHash(result.hash);

        const receipt = await result.wait();
        onReceipt && onReceipt(receipt);
      } catch (err: any) {
        let message = err.error?.message || err.message;
        if (typeof message === 'string') {
          message = message.replace(/^execution reverted:\s*/, '');
        }
        onFail && onFail(err, message);
      }
    },
    [eth, onClick, onFail, onReceipt, onRunTransaction],
  );

  return (
    <ButtonComponent {...rest} onClick={handleClick}>
      {typeof children === 'function' ? (
        children
      ) : (
        <ConditionalWrapper wrap={sparkling} wrapper={(c) => <Sparkles>{c}</Sparkles>}>
          <div style={{display: 'inline-flex', alignItems: 'center'}}>
            <b>{typeof children === 'function' ? children : children}</b>
            {eth && (
              <>
                &nbsp;<span>&#124;</span>&nbsp;{ethStr(eth)}&nbsp;
                <EthIcon size='small' type={props.type === 'primary' ? 'white' : 'color'} />
              </>
            )}
          </div>
        </ConditionalWrapper>
      )}
    </ButtonComponent>
  );
}
